<script lang="ts">
	import {
		Button,
		Text,
		Area,
		Field,
		Select,
		MultiCombo,
		Combo,
		DatePicker,
		ColorPicker,
		Slider,
	} from "@xbs/svelte-wx";

	import { getContext } from "svelte";
	import {
		elementsIds,
		defaultEditorConfig,
		isSameId,
	} from "@xbs/lib-kanban";
	import { form } from "@xbs/lib-svelte";
	import type { ICard, IApi, IEditorConfig } from "@xbs/lib-kanban";

	import FilesControl from "../lib/FilesControl.svelte";
	import UserIcon from "../lib/UserIcon.svelte";
	import DateRangePicker from "../lib/DateRangePicker.svelte";

	export let config: IEditorConfig = defaultEditorConfig;
	export let api: IApi;
	const { edit, editorShape, cards, dragItemId } = api.getReactiveState();

	$: editCard =
		!$dragItemId && $cards.find(c => isSameId(c.id, $edit?.cardId));

	$: {
		config = { ...defaultEditorConfig, ...config };
	}

	const _ = getContext<any>("wx-i18n").getGroup("kanban");

	let formValues: ICard;
	function saveForm() {
		api.exec("update-card", {
			card: { ...formValues },
			id: formValues.id,
		});
	}
	const values = form(
		{},
		val => {
			formValues = val;
			if (config.autoSave) {
				saveForm();
			}
		},
		{ debounce: config.debounce }
	);
	$: values.reset(setDefaults(editCard));

	function setDefaults(obj: any) {
		const copy = { ...obj };
		$editorShape.forEach(f => {
			if (typeof copy[f.key] === "undefined") {
				if (typeof copy[f.key] === "undefined") {
					if (f.type === "files") {
						copy[f.key] = [];
					} else if (f.type === "date") {
						copy[f.key] = null;
					} else if (f.type === "progress") {
						copy[f.key] = 0;
					} else {
						copy[f.key] = "";
					}
				}
			}
		});
		return copy;
	}
	function handleClose() {
		api.exec("set-edit", null);
	}

</script>

<div
	class="wx-editor"
	class:wx-editor-open={editCard}
	data-kanban-id={elementsIds.editor}
	on:click|stopPropagation>
	<div class="wx-editor-controls">
		<Button type="block wx-editor-btn" click={handleClose}>
			{_('Close')}
		</Button>
		{#if !config.autoSave}
			<Button type="block primary wx-editor-btn" click={saveForm}>
				{_('Save')}
			</Button>
		{/if}
	</div>

	{#if editCard}
		{#each $editorShape as field (field.id)}
			{#if field.type === 'text'}
				<Field label={_(field.label)} position="top" let:id>
					<Text
						{id}
						bind:value={$values[field.key]}
						focus
						{...field.config} />
				</Field>
			{:else if field.type === 'textarea'}
				<Field label={_(field.label)} position="top" let:id>
					<Area
						{id}
						bind:value={$values[field.key]}
						{...field.config} />
				</Field>
			{:else if field.type === 'progress'}
				<Field label={_(field.label)} position="top" let:id>
					<Slider
						{id}
						bind:value={$values[field.key]}
						min={0}
						{...field.config} />
				</Field>
			{:else if field.type === 'combo'}
				<Field label={_(field.label)} position="top" let:id>
					<Combo
						{id}
						options={field.values}
						bind:value={$values[field.key]}
						let:option
						{...field.config}>
						<div class="wx-combo-option">
							{#if option.color}
								<div
									class="wx-color"
									style="background:{option.color}" />
							{/if}
							{option.label}
						</div>
					</Combo>
				</Field>
			{:else if field.type === 'select'}
				<Field label={_(field.label)} position="top" let:id>
					<Select
						{id}
						bind:value={$values[field.key]}
						options={field.values}
						{...field.config} />
				</Field>
			{:else if field.type === 'color'}
				<Field label={_(field.label)} position="top" let:id>
					<ColorPicker
						{id}
						bind:value={$values[field.key]}
						colors={field.values}
						{...field.config} />
				</Field>
			{:else if field.type === 'multiselect'}
				<Field label={_(field.label)} position="top" let:id>
					<MultiCombo
						{id}
						bind:values={$values[field.key]}
						checkboxes
						options={field.values}
						let:option
						{...field.config}>
						<div class="wx-multiselect-option">
							<UserIcon data={option} />
							<span class="wx-multiselect-label">
								{option.label}
							</span>
						</div>
					</MultiCombo>
				</Field>
			{:else if field.type === 'date'}
				<Field label={_(field.label)} position="top" let:id>
					<DatePicker
						{id}
						format={'%m/%d/%Y'}
						bind:value={$values[field.key]}
						{...field.config} />
				</Field>
			{:else if field.type === 'dateRange'}
				<Field label={_(field.label)} position="top" let:id>
					<DateRangePicker
						{id}
						format={'%m/%d/%Y'}
						bind:start={$values[field.key.start]}
						bind:end={$values[field.key.end]} />
				</Field>
			{:else if field.type === 'files'}
				<Field label={_(field.label)} position="top">
					<FilesControl {field} {values} />
				</Field>
			{/if}
		{/each}
	{/if}
</div>

<style>
	.wx-editor {
		background: var(--wx-kanban-background-light);
		width: 0;
		min-width: 0;
		height: auto;
		transition: width 0.1s, min-width 0.1s;
		overflow-y: auto;
		overflow-x: hidden;
		z-index: 999999;
		box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.2),
			0px 1px 3px rgba(0, 0, 0, 0.1);
	}
	.wx-editor-open {
		padding: var(--wx-padding);
		width: var(--wx-kanban-editor-width);
		min-width: var(--wx-kanban-editor-width);
	}

	.wx-editor-controls {
		display: flex;
		width: 100%;
		margin-bottom: 15px;
		overflow: hidden;
	}
	:global(.wx-editor-controls > .wx-editor-btn:not(:last-child)) {
		margin-right: 10px;
	}
	.wx-multiselect-option {
		display: flex;
		align-items: center;
	}
	.wx-multiselect-label {
		padding: 0 5px;
	}
	.wx-combo-option {
		display: flex;
	}
	.wx-color {
		border-radius: 50%;
		width: 20px;
		height: 20px;
		margin-right: 10px;
	}
	/* [todo] create similar functionality in svelte-ws */
	:global(.wx-list-item) {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin: 5px 0;
	}
	:global(.wx-hover) {
		cursor: pointer;
	}
	:global(.wx-kanban > .wx-editor label) {
		padding-left: 0;
	}

</style>
